/**
 * Data-loading hook for Organisation Management.
 *
 * Extracted from OrganisationManagementPage.tsx (Sub-PR 6.3).
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { listOrganisations } from '../../services/admin'
import { listXeroInvoiceStates } from '../../services/licensing'
import type { Organisation, XeroInvoiceState } from '../../types'
import type { OrphanedXeroOrgId } from './types'
import { logger } from '../../utils/logger'

const FETCH_LIMIT = 100

interface UseOrganisationDataResult {
  organisations: Organisation[]
  xeroInvoices: XeroInvoiceState[]
  loading: boolean
  error: string | null
  setError: (msg: string | null) => void
  loadData: () => Promise<void>
  orphanedXeroOrgIds: OrphanedXeroOrgId[]
}

function computeOrphanedXeroOrgIds(
  organisations: Organisation[],
  xeroInvoices: XeroInvoiceState[]
): OrphanedXeroOrgId[] {
  const linkedXeroIds = new Set(
    organisations.filter((org) => org.xeroOrganisationId).map((org) => org.xeroOrganisationId)
  )
  const orphanedIds = new Map<string, OrphanedXeroOrgId>()
  for (const invoice of xeroInvoices) {
    if (linkedXeroIds.has(invoice.xeroOrganisationId)) continue
    const existing = orphanedIds.get(invoice.xeroOrganisationId)
    if (existing) {
      existing.invoiceCount++
      existing.totalAmount += invoice.amountPaid
    } else {
      orphanedIds.set(invoice.xeroOrganisationId, {
        id: invoice.xeroOrganisationId,
        invoiceCount: 1,
        totalAmount: invoice.amountPaid,
        currency: invoice.currencyCode,
      })
    }
  }
  return [...orphanedIds.values()]
}

export function useOrganisationData(): UseOrganisationDataResult {
  const { t } = useTranslation('admin')
  const [organisations, setOrganisations] = useState<Organisation[]>([])
  const [xeroInvoices, setXeroInvoices] = useState<XeroInvoiceState[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadData = useCallback(async (): Promise<void> => {
    try {
      setLoading(true)
      setError(null)
      const [orgsResponse, xeroResponse] = await Promise.all([
        listOrganisations({ limit: FETCH_LIMIT }),
        listXeroInvoiceStates({ limit: FETCH_LIMIT }),
      ])
      setOrganisations(orgsResponse.data)
      setXeroInvoices(xeroResponse.data)
    } catch (err) {
      setError(t('organisations.errors.loadFailed'))
      logger.error('useOrganisationData', 'Failed to load organisations', err)
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => {
    void loadData()
  }, [loadData])

  const orphanedXeroOrgIds = useMemo(
    () => computeOrphanedXeroOrgIds(organisations, xeroInvoices),
    [organisations, xeroInvoices]
  )

  return { organisations, xeroInvoices, loading, error, setError, loadData, orphanedXeroOrgIds }
}
